import React, { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import './EnquiriesAdmin.css'

/**
 * Admin panel for contact/join enquiries:
 *  - configure who receives notification emails (writes CONFIG#CONTACT)
 *  - view recorded submissions (contact + join), newest first
 */
function EnquiriesAdmin() {
  const [recipients, setRecipients] = useState('')
  const [savingCfg, setSavingCfg] = useState(false)
  const [cfgMsg, setCfgMsg] = useState(null)

  const [filter, setFilter] = useState('') // '' | 'contact' | 'join'
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    adminAPI.getContactConfig()
      .then((r) => setRecipients(r.join(', ')))
      .catch(() => {})
  }, [])

  useEffect(() => {
    let active = true
    ;(async () => {
      // Yield a microtask so the loading flag isn't set synchronously within
      // the effect body (avoids a cascading render); behaviour is unchanged.
      await Promise.resolve()
      if (!active) return
      setLoading(true)
      try {
        const s = await adminAPI.getSubmissions(filter)
        if (active) { setSubmissions(s); setError(null) }
      } catch (e) {
        if (active) setError(e.message)
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [filter])

  const saveConfig = async (e) => {
    e.preventDefault()
    setSavingCfg(true)
    setCfgMsg(null)
    try {
      const list = recipients.split(',').map((s) => s.trim()).filter(Boolean)
      const saved = await adminAPI.setContactConfig(list)
      setRecipients(saved.join(', '))
      setCfgMsg({ type: 'ok', text: 'Notification recipients saved.' })
    } catch (err) {
      setCfgMsg({ type: 'error', text: err.message })
    } finally {
      setSavingCfg(false)
    }
  }

  return (
    <div className="enquiries-admin">
      <section className="enquiries-config">
        <h2>Notification recipients</h2>
        <p className="muted">
          Comma-separated email addresses that receive contact and join enquiries.
          Each address must be verified in SES to receive mail while the account is in sandbox.
        </p>
        <form onSubmit={saveConfig} className="recipients-form">
          <input
            type="text"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="jamie_glendinning@work2fly.co.uk, someone@example.com"
          />
          <button type="submit" className="btn btn-primary" disabled={savingCfg}>
            {savingCfg ? 'Saving...' : 'Save'}
          </button>
        </form>
        {cfgMsg && <p className={`cfg-msg ${cfgMsg.type}`}>{cfgMsg.text}</p>}
      </section>

      <section className="enquiries-list">
        <div className="enquiries-list-header">
          <h2>Enquiries</h2>
          <div className="enquiries-filter">
            <label htmlFor="enq-filter">Show:</label>
            <select id="enq-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">All</option>
              <option value="contact">Contact</option>
              <option value="join">Join requests</option>
            </select>
          </div>
        </div>

        {loading && <p>Loading enquiries...</p>}
        {error && <p className="cfg-msg error">Could not load enquiries: {error}</p>}
        {!loading && !error && submissions.length === 0 && <p className="muted">No enquiries yet.</p>}

        {!loading && !error && submissions.length > 0 && (
          <table className="enquiries-table">
            <thead>
              <tr>
                <th>Date</th><th>Type</th><th>Name</th><th>Email</th><th>Details</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => (
                <tr key={s.submission_id}>
                  <td>{new Date(s.createdAt).toLocaleString('en-GB')}</td>
                  <td><span className={`enq-kind enq-${s.kind}`}>{s.kind}</span></td>
                  <td>{s.name}</td>
                  <td><a href={`mailto:${s.email}`}>{s.email}</a></td>
                  <td>
                    {s.phone && <div><strong>Phone:</strong> {s.phone}</div>}
                    {s.experience && <div><strong>Experience:</strong> {s.experience}</div>}
                    {s.subject && <div><strong>Subject:</strong> {s.subject}</div>}
                    {s.message && <div className="enq-message">{s.message}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}

export default EnquiriesAdmin
